import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BACKEND_URL } from '../config';

import {
  Building,
  CaretRight,
  CircleNotch,
  FloppyDisk,
  Graph,
  PencilSimple,
  Plus,
  Robot,
  Trash,
  UsersThree,
  X
} from '@phosphor-icons/react';
const OrgChartBuilder = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddRole, setShowAddRole] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [newRole, setNewRole] = useState({
    title: '',
    department: '',
    description: '',
    employee_name: '',
    supervisor_role_id: ''
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/agent/org-chart/roles`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRole = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/agent/org-chart/roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newRole)
      });

      if (response.ok) {
        fetchRoles();
        setShowAddRole(false);
        setNewRole({
          title: '',
          department: '',
          description: '',
          employee_name: '',
          supervisor_role_id: ''
        });
      }
    } catch (error) {
      console.error('Error adding role:', error);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/agent/org-chart/roles/${roleId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        fetchRoles();
      }
    } catch (error) {
      console.error('Error deleting role:', error);
    }
  };

  const buildHierarchy = (roles) => {
    // Build tree structure from flat roles array
    const roleMap = {};
    const roots = [];

    roles.forEach(role => {
      roleMap[role.role_id] = { ...role, children: [] };
    });

    roles.forEach(role => {
      if (role.supervisor_role_id && roleMap[role.supervisor_role_id]) {
        roleMap[role.supervisor_role_id].children.push(roleMap[role.role_id]);
      } else {
        roots.push(roleMap[role.role_id]);
      }
    });

    return roots;
  };

  const RoleCard = ({ role, level = 0 }) => {
    const hasSubordinates = role.children && role.children.length > 0;

    return (
      <div className="ml-${level * 8}">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="glass rounded-2xl p-6 mb-4 hover:bg-[rgba(255,255,255,0.08)] transition-all"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] flex items-center justify-center">
                  <UsersThree size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{role.title}</h3>
                  <p className="text-[#A0AEC0] text-sm">{role.department}</p>
                </div>
              </div>

              {role.employee_name && (
                <p className="text-[#A0AEC0] mb-2">
                  <span className="text-[#6366F1]">Assignee:</span> {role.employee_name}
                </p>
              )}

              {role.description && (
                <p className="text-[#A0AEC0] mb-4">{role.description}</p>
              )}

              <div className="flex flex-wrap gap-2">
                {role.linked_process_ids && role.linked_process_ids.length > 0 && (
                  <span className="px-3 py-1 rounded-full bg-[#14F195]/20 text-[#14F195] text-sm">
                    {role.linked_process_ids.length} Processes Linked
                  </span>
                )}
                {hasSubordinates && (
                  <span className="px-3 py-1 rounded-full bg-[#6366F1]/20 text-[#6366F1] text-sm">
                    {role.children.length} Subordinate{role.children.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => navigate(`/partner-pro/ai-workforce/agents/new?role=${role.role_id}`)}
                className="px-4 py-2 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold flex items-center gap-2 hover:scale-105 transition-transform"
              >
                <Robot size={16} />
                Generate Agent
              </button>
              <button
                onClick={() => handleDeleteRole(role.role_id)}
                className="p-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-[#EF4444]"
              >
                <Trash size={16} />
              </button>
            </div>
          </div>
        </motion.div>

        {hasSubordinates && (
          <div className="ml-8 border-l-2 border-[rgba(255,255,255,0.1)] pl-8">
            {role.children.map(child => (
              <RoleCard key={child.role_id} role={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
        <div className="flex items-center justify-center py-20">
          <CircleNotch size={48} className="text-[#6366F1] animate-spin" />
        </div>
      
    );
  }

  const hierarchy = buildHierarchy(roles);

  return (
      <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-12">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Graph size={32} className="text-[#6366F1]" />
                <h1 className="text-4xl font-bold text-white">Organization Chart</h1>
              </div>
              <p className="text-[#A0AEC0]">
                Define your organization structure and assign AI agents to roles
              </p>
            </div>

            <button
              onClick={() => setShowAddRole(true)}
              className="relative overflow-hidden rounded-full group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] to-[#EC4899]"></div>
              <span className="relative z-10 flex items-center gap-2 px-6 py-3 text-white font-bold">
                <Plus size={20} />
                Add Role
              </span>
            </button>
          </div>

          {/* Add Role Modal */}
          {showAddRole && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6"
              onClick={() => setShowAddRole(false)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="glass rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">Add New Role</h2>
                  <button
                    onClick={() => setShowAddRole(false)}
                    className="p-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)]"
                  >
                    <X size={20} className="text-white" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-white mb-2">Job Title *</label>
                    <input
                      type="text"
                      value={newRole.title}
                      onChange={(e) => setNewRole({ ...newRole, title: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                      placeholder="e.g., Customer Service Manager"
                    />
                  </div>

                  <div>
                    <label className="block text-white mb-2">Department *</label>
                    <input
                      type="text"
                      value={newRole.department}
                      onChange={(e) => setNewRole({ ...newRole, department: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                      placeholder="e.g., Customer Support"
                    />
                  </div>

                  <div>
                    <label className="block text-white mb-2">Employee Name (Optional)</label>
                    <input
                      type="text"
                      value={newRole.employee_name}
                      onChange={(e) => setNewRole({ ...newRole, employee_name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                      placeholder="e.g., John Smith"
                    />
                  </div>

                  <div>
                    <label className="block text-white mb-2">Description</label>
                    <textarea
                      value={newRole.description}
                      onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] resize-none"
                      rows={3}
                      placeholder="Describe the role responsibilities..."
                    />
                  </div>

                  <div>
                    <label className="block text-white mb-2">Reports To (Optional)</label>
                    <select
                      value={newRole.supervisor_role_id}
                      onChange={(e) => setNewRole({ ...newRole, supervisor_role_id: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                    >
                      <option value="">No Supervisor</option>
                      {roles.map(role => (
                        <option key={role.role_id} value={role.role_id}>
                          {role.title} ({role.department})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={handleAddRole}
                      disabled={!newRole.title || !newRole.department}
                      className="flex-1 px-6 py-3 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-bold disabled:opacity-50"
                    >
                      <FloppyDisk size={16} className="inline mr-2" />
                      Add Role
                    </button>
                    <button
                      onClick={() => setShowAddRole(false)}
                      className="px-6 py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Org Chart */}
          {hierarchy.length === 0 ? (
            <div className="glass rounded-3xl p-12 text-center">
              <Building size={64} className="mx-auto mb-6 text-[#6366F1]" />
              <h3 className="text-2xl font-bold text-white mb-3">No Roles Yet</h3>
              <p className="text-[#A0AEC0] mb-6">
                Start building your organization chart by adding roles
              </p>
              <button
                onClick={() => setShowAddRole(true)}
                className="relative overflow-hidden rounded-full group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] to-[#EC4899]"></div>
                <span className="relative z-10 flex items-center gap-2 px-6 py-3 text-white font-bold">
                  <Plus size={20} />
                  Add First Role
                </span>
              </button>
            </div>
          ) : (
            <div>
              {hierarchy.map(role => (
                <RoleCard key={role.role_id} role={role} />
              ))}
            </div>
          )}
      </div>
  );
};

export default OrgChartBuilder;
